import { Component, computed, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { PaginatorState } from 'primeng/paginator';
import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { PrestadorHistoricoProtocolo, PrestadorHistoricoTramitacao } from '@/core/api/sicom-api/registro/prestador/prestador-historico.model';
import { PrestadorHistoricoService } from '@/core/api/sicom-api/registro/prestador/prestador-historico.service';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { DateUtils } from '@/shared/utils/date-utils';
import { Dialog } from 'primeng/dialog';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
@Component({ selector: 'app-protocolos-prestador', standalone: true, imports: [DialogoPadraoDirective, CardContent, CustomTableComponent, Dialog], templateUrl: './protocolos-prestador.html' })
export class ProtocolosPrestador {
    pessoa = input<PessoaDomain>();
    private readonly destroyRef = inject(DestroyRef);
    private readonly service = inject(PrestadorHistoricoService);
    readonly protocolos = signal<PrestadorHistoricoProtocolo[]>([]);
    readonly tramitacoes = signal<PrestadorHistoricoTramitacao[]>([]);
    readonly protocoloSelecionado = signal<PrestadorHistoricoProtocolo | null>(null);
    readonly tramitacaoSelecionada = signal<PrestadorHistoricoTramitacao | null>(null);
    readonly protocolosFirst = signal(0);
    readonly protocolosRows = signal(10);
    readonly protocolosTotalRecords = signal(0);
    readonly tramitacoesFirst = signal(0);
    readonly tramitacoesRows = signal(10);
    readonly tramitacoesTotalRecords = signal(0);
    readonly carregandoProtocolos = signal(false);
    readonly carregandoTramitacoes = signal(false);
    readonly dialogTramitacoesVisivel = signal(false);
    readonly dialogDespachoVisivel = signal(false);
    readonly tramitacoesTabela = computed(() => this.tramitacoes().map((item) => ({ ...item, dataTramitacao: DateUtils.formatDate(item.dataTramitacao), dataConfirmacao: DateUtils.formatDate(item.dataConfirmacao) })));
    readonly metadataProtocolos: TableMetadata = {
        headers: {
            ano: { type: 'text', value: 'Ano', default: '-', hidden: false },
            numero: { type: 'text', value: 'Número', default: '-', hidden: false },
            tipoProtocolo: { type: 'text', value: 'Tipo', default: '-', hidden: false },
            assunto: { type: 'text', value: 'Assunto', default: '-', hidden: false },
            data: { type: 'text', value: 'Abertura', default: '-', hidden: true, hiddenOnCard: true },
            situacao: { type: 'text', value: 'Situação', default: '-', hidden: true, hiddenOnCard: true },
            localizacao: { type: 'text', value: 'Localização', default: '-', hidden: true, hiddenOnCard: true }
        },
        title: 'assunto',
        actions: {
            tramitacoes: {
                label: 'Tramitações',
                icon: 'pi pi-list',
                color: 'info',
                command: (protocolo: PrestadorHistoricoProtocolo) => this.abrirTramitacoes(protocolo)
            }
        }
    };
    readonly metadataTramitacoes: TableMetadata = {
        headers: {
            responsavel: { type: 'text', value: 'Responsável', default: '-', hidden: false },
            dataTramitacao: { type: 'text', value: 'Tramitação', default: '-', hidden: false },
            dataConfirmacao: { type: 'text', value: 'Confirmação', default: '-', hidden: true, hiddenOnCard: true },
            status: { type: 'text', value: 'Status', default: '-', hidden: false },
            destino: { type: 'text', value: 'Destino', default: '-', hidden: true, hiddenOnCard: true },
            origem: { type: 'text', value: 'Origem', default: '-', hidden: true, hiddenOnCard: true },
            situacao: { type: 'text', value: 'Situação', default: '-', hidden: true, hiddenOnCard: true }
        },
        title: 'responsavel',
        actions: {
            despacho: {
                label: 'Despacho',
                icon: 'pi pi-file-edit',
                color: 'secondary',
                command: (tramitacao: PrestadorHistoricoTramitacao) => this.abrirDespacho(tramitacao)
            }
        }
    };
    constructor() {
        effect(() => {
            const pessoa = this.pessoa();
            if (pessoa?.id && pessoa.uf) untracked(() => this.carregarProtocolos());
        });
    }
    abrirTramitacoes(protocolo: PrestadorHistoricoProtocolo): void {
        this.protocoloSelecionado.set(protocolo);
        this.tramitacaoSelecionada.set(null);
        this.dialogDespachoVisivel.set(false);
        this.tramitacoes.set([]);
        this.tramitacoesFirst.set(0);
        this.tramitacoesTotalRecords.set(0);
        this.dialogTramitacoesVisivel.set(true);
        this.carregarTramitacoes(protocolo.idProtocolo);
    }

    abrirDespacho(tramitacao: PrestadorHistoricoTramitacao): void {
        this.tramitacaoSelecionada.set(tramitacao);
        this.dialogDespachoVisivel.set(true);
    }

    fecharTramitacoes(): void {
        this.dialogTramitacoesVisivel.set(false);
        this.dialogDespachoVisivel.set(false);
        this.tramitacaoSelecionada.set(null);
    }

    fecharDespacho(): void {
        this.dialogDespachoVisivel.set(false);
        this.tramitacaoSelecionada.set(null);
    }
    onPaginaProtocolosAlterada(event: PaginatorState): void {
        this.protocolosFirst.set(event.first ?? 0);
        this.protocolosRows.set(event.rows ?? 10);
        this.carregarProtocolos();
    }
    onPaginaTramitacoesAlterada(event: PaginatorState): void {
        const protocolo = this.protocoloSelecionado();
        this.tramitacoesFirst.set(event.first ?? 0);
        this.tramitacoesRows.set(event.rows ?? 10);
        if (protocolo) this.carregarTramitacoes(protocolo.idProtocolo);
    }
    private carregarProtocolos(): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf) return;
        this.carregandoProtocolos.set(true);
        this.service
            .listarProtocolos(pessoa.id, pessoa.uf, Math.floor(this.protocolosFirst() / this.protocolosRows()), this.protocolosRows())
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.carregandoProtocolos.set(false))
            )
            .subscribe({
                next: (response) => {
                    this.protocolos.set(response.content ?? []);
                    this.protocolosTotalRecords.set(response.totalElements ?? 0);
                },
                // Sem isto, a falha na troca de prestador deixaria os dados do anterior na tela.
                error: () => {
                    this.protocolos.set([]);
                    this.protocolosTotalRecords.set(0);
                }
            });
    }
    private carregarTramitacoes(idProtocolo: number): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf) return;
        this.carregandoTramitacoes.set(true);
        this.service
            .listarTramitacoes(pessoa.id, pessoa.uf, idProtocolo, Math.floor(this.tramitacoesFirst() / this.tramitacoesRows()), this.tramitacoesRows())
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.carregandoTramitacoes.set(false))
            )
            .subscribe((response) => {
                this.tramitacoes.set(response.content ?? []);
                this.tramitacoesTotalRecords.set(response.totalElements ?? 0);
            });
    }
}
